import React, { useState, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { noop, isEqual, omit } from 'underscore';

import {
  useDefaultEditorStyle,
  getUrlFromStockImage,
  getThumbnailUrlForFacebookPhoto,
} from '@bingads-webui-campaign-react/smart-component-util';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { InstrumentationServiceContext } from '@bingads-webui-campaign-react/smart-page-context';

import { getImageOptionsForPivot } from './components/common-image-options-for-pivot';
import { ImageOptionsKey } from './components/consts';
import { PivotImageEditorContextProvider } from './smart-pivot-image-editor-context';

const getStyles = () => ({
  connectFacebookMsg: {
    marginTop: 8,
  },
  loading: {
    height: '445px',
  },
});

export const SmartPivotImageEditor = ({
  className,
  imageOptionsConfig,
  i18n,
  onLocalFileSelected,
  onFacebookItemSelectionChanged,
  onStockImageItemSelectionChanged,
  onSelectionChanged,
  gotoSettings,
  onPivotLinkClicked,
  onFinalSelectedPivotChanged,
  itemsPerPage,
  isMultiple,
  isFbSupported,
  fbPhotos,
  isLoadingFbPhotos,
  defaultKeywords,
  existingImages,
  itemToUnselect,
  setItemToUnselect,
}) => {
  // State to store all selection with order from each pivot child
  const [allSelection, setAllSelection] = useState([]);
  // State to manage itemToUnselect internally
  const [itemToUnselectInternal, setItemToUnselectInternal] = useState(null);
  // Will change when once click on pivot, diff from finalSelectedPivot
  const [selectedPivot, setSelectedPivot] = useState(null);
  // Final selected pivot only get changed when item in that pivot is selected
  const [finalSelectedPivot, setFinalSelectedPivot] = useState(null);

  const logger = useContext(InstrumentationServiceContext);
  const classes = useDefaultStyles(getStyles);

  const handleOnPivotClicked = useCallback((item) => {
    const { props: { itemKey } } = item;
    onPivotLinkClicked(itemKey);
    setSelectedPivot(itemKey);
    logger.logTrace(`SmartPivotImageEditor|PivotItemClicked|${itemKey}`);
  }, [onPivotLinkClicked, logger]);

  const {
    editorPivotStyleContentClassName,
    uploadButtonClassName,
    infoMessageBarClassName,
  } = useDefaultEditorStyle();

  const handleOnSelectionChanged = useCallback((newSelection) => {
    onSelectionChanged(newSelection);
    setAllSelection(newSelection);
  }, [onSelectionChanged]);

  const handleOnFinalSelectedPivotChanged = useCallback((key) => {
    onFinalSelectedPivotChanged(key);
    setFinalSelectedPivot(key);
  }, [onFinalSelectedPivotChanged]);

  const handleResetItemToUnselect = useCallback(() => {
    setItemToUnselect(null);
    setItemToUnselectInternal(null);
  }, [setItemToUnselect]);

  /**
   * item => the new changed item
   * changeSource => what kind of update it is
   * isLocalFile
   */
  const updateSelection = useCallback((item, changeSource, isLocalFile = false) => {
    const previousFinalSelectedPivot = finalSelectedPivot;
    handleOnFinalSelectedPivotChanged(changeSource);

    if (!isMultiple) {
      // TODO make upload local support keep the error image, but return error mark
      // Currently when we upload a image from local which contains error, item will be null
      if (!item) {
        return;
      }
      // In single selection, allSelection should contains only one item
      const [prevItem] = allSelection;
      handleOnSelectionChanged([item]);

      // Only unselect prev item when it was another tab
      if (prevItem && (selectedPivot !== previousFinalSelectedPivot)) {
        // Since this is an internal change, no need to notify parent
        setItemToUnselectInternal(prevItem);
      }
      return;
    }

    let predicate = selection => selection.file;
    if (changeSource === ImageOptionsKey.DEFAULT) {
      predicate = selection => getUrlFromStockImage(selection) === getUrlFromStockImage(item);
    } else if (changeSource === ImageOptionsKey.FACEBOOKIMPORT) {
      predicate = selection =>
        getThumbnailUrlForFacebookPhoto(selection) === getThumbnailUrlForFacebookPhoto(item);
    }

    const index = allSelection.findIndex(predicate);
    const newSelection = [...allSelection];

    if (isLocalFile) {
      // Remove the previously added local image, currently support upload one image from local

      if (index > -1 && item) {
        newSelection.splice(index, 1);
      }
      if (item) {
        newSelection.push(item);
      }
    } else if (item.selected && index === -1) {
      // Add to selection if not added yet
      newSelection.push(item);
    } else if (!item.selected && index > -1) {
      // Remove from selection if already added and currently not selected
      newSelection.splice(index, 1);
    }

    handleOnSelectionChanged(newSelection);
  }, [
    finalSelectedPivot,
    handleOnFinalSelectedPivotChanged,
    isMultiple,
    allSelection,
    handleOnSelectionChanged,
    selectedPivot,
  ]);

  const onHandleStockImageItemSelectionChanged = useCallback((item) => {
    updateSelection(item, ImageOptionsKey.DEFAULT);
    onStockImageItemSelectionChanged(item);
  }, [updateSelection, onStockImageItemSelectionChanged]);

  const onHandleFacebookItemSelectionChanged = useCallback((item) => {
    updateSelection(item, ImageOptionsKey.FACEBOOKIMPORT);
    onFacebookItemSelectionChanged(item);
  }, [updateSelection, onFacebookItemSelectionChanged]);

  const onHandleLocalFileSelected = useCallback((localImage) => {
    updateSelection(localImage, ImageOptionsKey.UPLOAD, true /* IsLocalFile */);
    onLocalFileSelected(localImage);
  }, [updateSelection, onLocalFileSelected]);

  const finalImageOptions = getImageOptionsForPivot({
    i18n,
    classes: {
      editorPivotStyleContentClassName,
      uploadButtonClassName,
      infoMessageBarClassName,
      connectFacebookMsg: classes.connectFacebookMsg,
      loading: classes.loading,
    },
    imageOptionsConfig,
    onFileSelected: onHandleLocalFileSelected,
    onStockImageItemSelectionChanged: onHandleStockImageItemSelectionChanged,
    onFacebookItemSelectionChanged: onHandleFacebookItemSelectionChanged,
    gotoSettings,
    fbPhotos,
    itemsPerPage,
    isMultiple,
    isFbSupported,
    isLoadingFbPhotos,
    defaultKeywords,
    existingImages,
    itemToUnselect: itemToUnselectInternal,
    resetItemToUnselect: handleResetItemToUnselect,
  });

  useEffect(() => {
    if (itemToUnselect) {
      // Every time itemToUnselect get changed by parent, we store it in internal and pass down
      setItemToUnselectInternal(itemToUnselect);
    }
  }, [itemToUnselect]);

  useEffect(() => {
    if (itemToUnselect) {
      const newSelection = allSelection.filter(selection => !isEqual(omit(itemToUnselect, 'selected'), omit(selection, 'selected')));
      handleOnSelectionChanged(newSelection);
      setItemToUnselect(null);
    }
  }, [allSelection, handleOnSelectionChanged, itemToUnselect, setItemToUnselect]);

  return (
    <PivotImageEditorContextProvider selection={allSelection}>
      <div className={className}>
        <Pivot onLinkClick={handleOnPivotClicked}>
          {
            finalImageOptions.map(option => (
              <PivotItem
                headerText={i18n.getString(option.text)}
                itemKey={option.key}
                key={option.key}
                alwaysRender
              >
                {option.onRenderItemLink()}
              </PivotItem>
            ))
          }
        </Pivot>
      </div>
    </PivotImageEditorContextProvider>
  );
};

SmartPivotImageEditor.propTypes = {
  className: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  imageOptionsConfig: PropTypes.shape({
    [ImageOptionsKey.DEFAULT]: PropTypes.shape({
      text: PropTypes.string,
    }).isRequired,
    [ImageOptionsKey.FACEBOOKIMPORT]: PropTypes.shape({
      text: PropTypes.string,
    }).isRequired,
  }),
  onLocalFileSelected: PropTypes.func,
  onStockImageItemSelectionChanged: PropTypes.func,
  onFacebookItemSelectionChanged: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  gotoSettings: PropTypes.func,
  // onPivotLinkClicked: will notify which link key has been selected
  onPivotLinkClicked: PropTypes.func,
  // onFinalSelectedPivotChanged: will notify when item in that pivot is selected
  onFinalSelectedPivotChanged: PropTypes.func,
  itemsPerPage: PropTypes.number,
  isMultiple: PropTypes.bool,
  isFbSupported: PropTypes.bool,
  fbPhotos: PropTypes.arrayOf(PropTypes.shape({})),
  isLoadingFbPhotos: PropTypes.bool.isRequired,
  // defaultKeywords: keyword used to search stock image
  defaultKeywords: PropTypes.arrayOf(PropTypes.any),
  // existingImages: images that already added in config, not the pending selections
  existingImages: PropTypes.arrayOf(PropTypes.any),
  // itemToUnselect: set by parent, top down notification, to tell pivot child to unselect this item
  itemToUnselect: PropTypes.shape({}),
  // setItemToUnselect: when one of the child handles itemToUnselect correctly, call this to reset
  setItemToUnselect: PropTypes.func,
};

SmartPivotImageEditor.defaultProps = {
  className: null,
  imageOptionsConfig: {
    [ImageOptionsKey.DEFAULT]: { text: '' },
    [ImageOptionsKey.FACEBOOKIMPORT]: { text: '' },
  },
  onStockImageItemSelectionChanged: noop,
  onFacebookItemSelectionChanged: noop,
  onLocalFileSelected: noop,
  onSelectionChanged: noop,
  gotoSettings: noop,
  onPivotLinkClicked: noop,
  onFinalSelectedPivotChanged: noop,
  itemsPerPage: 8,
  isMultiple: true,
  isFbSupported: false,
  fbPhotos: [],
  defaultKeywords: [],
  existingImages: [],
  itemToUnselect: null,
  setItemToUnselect: noop,
};
